/**
 * postAudience.js — who may read a feed post, and how a feed query proves it. (RW-2957)
 *
 * A post's `office` is its AUDIENCE, not a tag. firestore.rules (posts read,
 * `canReadPostAudience`) lets a signed-in tenant member read a post when ANY of:
 *   - the post is All Company — `office` is one of ALL_COMPANY_OFFICE_VALUES
 *     ('all', plus the legacy 'All' / 'All Offices' / ''); a post with NO
 *     office field counts only for a single-doc get (a list query can't prove
 *     it — every writer stamps 'all' now, and the backfill stamped the past);
 *   - the post is a group post (`groupId` set) — a group's audience is the group;
 *   - the reader wrote it;
 *   - the reader is an admin (claim, or roles[] holding admin / super_admin);
 *   - the reader has a `feedOffices` list (admin-set, Clint 10-06 Q3): their
 *     own office or one on the list — the list REPLACES the default below,
 *     for staff too;
 *   - no list: the reader is staff (roles[] broker / tc), has no office
 *     (`users/{uid}.officeId` missing, not a string, or ''), or their
 *     `officeId` equals the post's `office`.
 *
 * Firestore checks rules against the QUERY, not the rows it returns: a list
 * query is allowed only when every document it COULD return passes. So a
 * non-staff reader with an office must say so in the query —
 * `where('office', 'in', postOfficeScope(viewer))` — and staff and no-office
 * readers query unconstrained (their branch of the rule passes for any row).
 *
 * The viewer is built ONCE from the reader's own user doc (App.js, the same
 * users/{uid} the rule reads), so the client and the rule judge the same fields.
 * The client may only ever be NARROWER than the rule — a query constrained when
 * the rule would have let it through is fine; the reverse empties the feed. So:
 *   - staff here is roles[] only (the rule also honours the admin CLAIM, which
 *     the client cannot see — such a user just gets the narrower query);
 *   - `officeId` is read verbatim, never canonicalized — the rule compares the
 *     raw string, so 'New Orleans' must not be "fixed" to 'new-orleans' here.
 *
 * Server twin: functions/lib/postAudience.js (+ functions-scheduled/lib/),
 * which applies the same predicate to digests and mention notifications. The
 * constants below are asserted equal to the twin AND to firestore.rules by
 * postAudience.test.js.
 */

// Values of post.office that mean "everyone". 'all' is what every writer
// stamps from RW-2957 on; the rest are legacy spellings still in the data
// ('All' was buildPostPayload's default, 'All Offices' the old display label).
export const ALL_COMPANY_OFFICE_VALUES = ['all', 'All', 'All Offices', ''];

// users/{uid}.roles[] entries that see every office (Clint, 10-06: admin /
// broker / TC). super_admin is an admin.
export const POST_AUDIENCE_STAFF_ROLES = ['super_admin', 'admin', 'broker', 'tc'];
// Admins see every office even with a feedOffices list (no self-lockout).
export const POST_AUDIENCE_ADMIN_ROLES = ['super_admin', 'admin'];
// The rule caps feedOffices at this many ids (users write rule).
export const FEED_OFFICES_MAX = 20;

// The rule's permanent emergency fallback (isPermanentSuperAdmin) is staff.
const PERMANENT_SUPER_ADMIN_EMAIL = 'clint@reverealtors.com';

/**
 * Does this post.office value read as "All Company" on a card? Missing and
 * null do (nothing scoped it). Labels only — canViewerReadPost is the access
 * mirror and is stricter, exactly like the rule.
 */
export function isAllCompanyOffice(office) {
  if (office === undefined || office === null) return true;
  return ALL_COMPANY_OFFICE_VALUES.includes(office);
}

/**
 * The viewer, from the reader's own users/{uid} doc (null/undefined doc = no
 * doc = no office, not staff — the rule reads a missing doc the same way).
 * @returns {{ admin: boolean, staff: boolean, officeId: string, feedOffices: string[] | null }}
 */
export function postViewerFromUserDoc(userDoc, { email } = {}) {
  const d = userDoc && typeof userDoc === 'object' ? userDoc : {};
  const roles = Array.isArray(d.roles) ? d.roles : [];
  const admin = roles.some((r) => POST_AUDIENCE_ADMIN_ROLES.includes(r))
    || String(email || '').toLowerCase() === PERMANENT_SUPER_ADMIN_EMAIL;
  const staff = admin || roles.some((r) => POST_AUDIENCE_STAFF_ROLES.includes(r));
  const feedOffices = Array.isArray(d.feedOffices)
    ? d.feedOffices.filter((o) => typeof o === 'string' && o !== '')
    : null;
  return { admin, staff, officeId: typeof d.officeId === 'string' ? d.officeId : '', feedOffices };
}

/**
 * The viewer for a surface's `user` prop. App.js puts `postViewer` on the user
 * object it hands down: null while the user doc is still loading (the caller
 * must wait — an unconstrained query fired too early is denied for an office
 * reader). A user object without the key (tests, a stand-in) falls back to
 * its own officeId, never staff — narrower than the rule, so always allowed.
 * @returns {{ staff: boolean, officeId: string } | null}
 */
export function postViewerFor(user) {
  if (!user) return null;
  if (user.postViewer === null) return null;
  if (user.postViewer && typeof user.postViewer === 'object') return user.postViewer;
  return { admin: false, staff: false, officeId: typeof user.officeId === 'string' ? user.officeId : '', feedOffices: null };
}

/**
 * The `office in [...]` values a feed query must carry for this viewer, or
 * null when the viewer sees every office (staff / no office) and the query
 * stays unconstrained. At most 5 values (Firestore allows 30 per `in`).
 */
export function postOfficeScope(viewer) {
  if (!viewer || viewer.admin) return null;
  if (Array.isArray(viewer.feedOffices)) {
    // A list replaces the default, for staff too: All Company + own office +
    // the listed offices. Capped so the `in` stays under Firestore's 30.
    const extra = [viewer.officeId, ...viewer.feedOffices.slice(0, FEED_OFFICES_MAX)].filter((o) => typeof o === 'string' && o !== '');
    return [...new Set([...ALL_COMPANY_OFFICE_VALUES, ...extra])];
  }
  if (viewer.staff || !viewer.officeId) return null;
  return ALL_COMPANY_OFFICE_VALUES.includes(viewer.officeId)
    ? [...ALL_COMPANY_OFFICE_VALUES]
    : [...ALL_COMPANY_OFFICE_VALUES, viewer.officeId];
}

/** A stable string for effect deps: 'pending' | 'every' | the scope values. */
export function postScopeKey(viewer) {
  if (!viewer) return 'pending';
  const scope = postOfficeScope(viewer);
  return scope ? scope.join('|') : 'every';
}

/**
 * Mirror of the rule for ONE post already in hand, as a single-doc get sees
 * it (client-side checks only — the rule is the gate). `uid` is the reader's
 * own uid.
 */
export function canViewerReadPost(viewer, post, uid) {
  if (!post) return false;
  if (ALL_COMPANY_OFFICE_VALUES.includes(post.office)) return true;
  if (!('office' in post)) return true; // the rule's single-doc branch
  if (post.groupId != null) return true;
  if (uid && post.authorUid === uid) return true;
  if (!viewer) return false;
  if (viewer.admin) return true;
  if (Array.isArray(viewer.feedOffices)) {
    return (!!viewer.officeId && post.office === viewer.officeId) || viewer.feedOffices.includes(post.office);
  }
  if (viewer.staff || !viewer.officeId) return true;
  return post.office === viewer.officeId;
}

/**
 * The `office` a NEW post is written with. A group post's audience is its
 * group, so it is stored as 'all' (else an office reader's feed query, which
 * asks for `office in [all…, mine]`, would drop a room post from another
 * office's composer). Any All Company spelling, blank or missing → 'all'.
 */
export function officeForNewPost({ office, groupId } = {}) {
  if (groupId) return 'all';
  if (typeof office !== 'string' || isAllCompanyOffice(office) || office.trim() === '') return 'all';
  return office;
}
